import { Color, Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Tooltip from 'dashboard/components/Tooltip';
import {
  ConditionalRule,
  ConditionalRuleValueConfig,
  getConditionalRuleParts,
  getOperatorDisplayName,
  JsonLogicValue,
  parseFieldPath,
  RuleValueTypes,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { getRuleSideDisplay, isEmptyRule } from 'dashboard/lib/workflows/conditional';
import fieldMetas from 'dashboard/lib/workflows/fieldMetas';
import { WorkflowFieldKey } from 'dashboard/models/workflow';
import { paragraphMixins } from 'design-system/mixins/text';
import { JsonSchema } from 'lib/json-schema';
import { hasNoPossibilities, TokensToNode } from 'lib/workflows';

const FieldDataPill = styled.span<{ rejected: boolean }>`
  background-color: ${({ rejected }) => (rejected ? Color.CoreSlate100 : Color.CoreSlate200)};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  overflow: hidden;
  text-overflow: ellipsis;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const ValueData = styled.p`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  &:hover {
    text-overflow: clip;
    white-space: normal;
    word-break: break-all;
  }
`;

const OperatorPill = styled.span<{ rejected: boolean }>`
  background-color: ${({ rejected }) => (rejected ? Color.CoreSlate100 : Color.CoreSlate200)};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const PaddedContainer = styled.div<{ hasError: boolean }>`
  padding-top: ${Spacing[2]};
  padding-bottom: ${({ hasError }) => (hasError ? 0 : `${Spacing[2]}`)};
`;

const FIELD_KEYS_TRUNCATE_VALUES: WorkflowFieldKey[] = [
  WorkflowFieldKey.Country,
  WorkflowFieldKey.CountryFromSession,
  WorkflowFieldKey.SelectedCountry,
  WorkflowFieldKey.UsState,
  WorkflowFieldKey.PhoneCountryCode,
];

const FIELD_KEYS_SEPARATE_VALUES: WorkflowFieldKey[] = [WorkflowFieldKey.Tags];

interface RulePillProps {
  andRule: ConditionalRule;
  hasError: boolean;
  rejected?: boolean;
  tokensToSteps: TokensToNode;
  usableObjects: UsableObjectConfig[];
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
}

const RulePill: VFC<RulePillProps> = ({
  andRule,
  tokensToSteps,
  usableObjects,
  jsonSchema,
  tokensToDisplayValue,
  hasError = false,
  rejected = false,
}) => {
  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(andRule);

  if (isEmptyRule(valueLeftConfig, valueRightConfig)) {
    return (
      <PaddedContainer hasError={hasError} role='listitem'>
        <FieldDataPill rejected={rejected}>Empty Rule</FieldDataPill>
      </PaddedContainer>
    );
  }

  const renderOneSide = (
    ruleValueConfig: ConditionalRuleValueConfig,
    otherConfig?: ConditionalRuleValueConfig,
  ) => {
    const otherPath = otherConfig?.type === RuleValueTypes.Field ? otherConfig.paths[0] : '';
    const { criteriaVariableKey } = parseFieldPath(otherPath, usableObjects);

    const formatValues = (valueToFormat: JsonLogicValue) => {
      const stringifiedValue = Array.isArray(valueToFormat)
        ? valueToFormat
        : String(valueToFormat).replace(/,/g, ', ');
      return (
        fieldMetas[criteriaVariableKey]?.displayValueMapper(stringifiedValue) || stringifiedValue
      );
    };

    const renderValues = (valueToRender: JsonLogicValue | null) => {
      if (
        FIELD_KEYS_TRUNCATE_VALUES.includes(criteriaVariableKey) &&
        Array.isArray(valueToRender) &&
        valueToRender.length > 2
      ) {
        const tooltip = valueToRender
          .slice(2)
          .map((val, index) => <p key={`${val}__${index}`}>{formatValues(val)}</p>);
        return (
          <FieldDataPill rejected={rejected}>
            <Tooltip tooltip={tooltip} placement='top'>
              {formatValues(valueToRender.slice(0, 2))} and {valueToRender.length - 2} more
            </Tooltip>
          </FieldDataPill>
        );
      }

      if (
        FIELD_KEYS_SEPARATE_VALUES.includes(criteriaVariableKey) &&
        Array.isArray(valueToRender)
      ) {
        return valueToRender.sort().map((v, i) => (
          <FieldDataPill key={i} rejected={rejected}>
            {formatValues(v)}
          </FieldDataPill>
        ));
      }

      return (
        <FieldDataPill rejected={rejected}>
          <ValueData>{valueToRender != null ? formatValues(valueToRender) : ''}</ValueData>
        </FieldDataPill>
      );
    };

    return renderValues(
      getRuleSideDisplay({
        jsonSchema,
        otherConfig,
        ruleValueConfig,
        tokensToDisplayValue,
        tokensToSteps,
        usableObjects,
      }),
    );
  };

  return (
    <PaddedContainer hasError={hasError} role='listitem' aria-label='Conditional rule'>
      {renderOneSide(valueLeftConfig)}
      <OperatorPill rejected={rejected}>
        {operator != null ? getOperatorDisplayName(operator) : ''}{' '}
      </OperatorPill>
      {!hasNoPossibilities(operator) &&
        valueRightConfig &&
        renderOneSide(valueRightConfig, valueLeftConfig)}
    </PaddedContainer>
  );
};

export default RulePill;
